import { useEffect, useState, useRef } from 'react';
import { fetchSettings } from '../services/api';

// Note: Sun is omitted as it's the reference point in heliocentric charts
const MAIN_PLANETS = ['mercury', 'venus', 'earth', 'mars', 'jupiter', 'saturn', 'uranus', 'neptune', 'pluto'];

const HIGHER_OCTAVE_PLANETS = [
  'ceres',
  'pallas',
  'juno',
  'vesta',
  'chiron',
  'pholus',
  'makemake',
  'eris',
  'quaoar',
  'sedna',
  'haumea',
  'ixion',
  'varuna',
  'gonggong',
  'orcus',
];

const buildFallbackMatrix = (planetKeys, defaults) =>
  planetKeys.reduce((acc, planet) => {
    acc[planet] = { ...defaults };
    return acc;
  }, {});

const clone = (obj) => JSON.parse(JSON.stringify(obj));

const fallbackMain = buildFallbackMatrix(MAIN_PLANETS, {
  conjunction: 6.5,
  opposition: 6.5,
  square: 5.5,
  trine: 5.5,
  sextile: 3.5,
  semi_sextile: 2.0,
  semi_square: 2.0,
  sesquiquadrate: 2.0,
  quincunx: 2.5,
});

const fallbackHigher = buildFallbackMatrix(HIGHER_OCTAVE_PLANETS, {
  conjunction: 4,
  opposition: 4,
  square: 3.5,
  trine: 3.5,
  sextile: 2.5,
  semi_sextile: 1.5,
  semi_square: 1.5,
  sesquiquadrate: 1.5,
  quincunx: 2,
});

const ALLOWED_PROVIDER_IDS = ['openai', 'google'];

const DEFAULT_ALLOWED_PROVIDERS = [
  { id: 'openai', name: 'OpenAI' },
  { id: 'google', name: 'Google Gemini' },
];

const ensureAllowedProviders = (list = []) => {
  const filtered = list.filter((provider) => ALLOWED_PROVIDER_IDS.includes(provider.id));
  const missingDefaults = DEFAULT_ALLOWED_PROVIDERS.filter(
    (defaultProvider) => !filtered.some((provider) => provider.id === defaultProvider.id),
  );
  return [...filtered, ...missingDefaults];
};

export const useSettingsInitializer = ({
  setProviders,
  setActiveProvider,
  setOrbSettings,
  setCostSummary,
}) => {
  // Start with false since we set defaults immediately - no blocking
  const [initializingSettings] = useState(false);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    // Prevent duplicate calls
    if (hasLoadedRef.current) {
      return;
    }
    
    let isMounted = true;
    hasLoadedRef.current = true;
    
    // Set defaults immediately so UI can render right away
    setProviders(DEFAULT_ALLOWED_PROVIDERS);
    setActiveProvider('openai');
    setOrbSettings({
      default: {
        mainPlanets: clone(fallbackMain),
        higherOctavePlanets: clone(fallbackHigher),
      },
      current: {
        mainPlanets: clone(fallbackMain),
        higherOctavePlanets: clone(fallbackHigher),
      },
    });
    setCostSummary(null);
    
    // Then try to load settings in the background
    const loadSettings = async () => {
      try {
        const settings = await fetchSettings();
        if (!isMounted) return;
        
        const providerList = settings.providers?.providers || settings.providers || [];
        const allowedProviders = ensureAllowedProviders(providerList);
        setProviders(allowedProviders);
        const requestedDefault = settings.providers?.defaultProvider;
        const defaultProvider = ALLOWED_PROVIDER_IDS.includes(requestedDefault)
          ? requestedDefault
          : allowedProviders[0]?.id;
        if (defaultProvider) {
          setActiveProvider(defaultProvider);
        }
        if (settings.orbs) {
          setOrbSettings(settings.orbs);
        }
        if (settings.costs) {
          setCostSummary(settings.costs);
        }
      } catch (err) {
        // Silently fail - we already have defaults set
        console.error('Failed to load settings (using defaults):', err);
      }
    };

    // Load settings asynchronously without blocking UI
    loadSettings();
    
    return () => {
      isMounted = false;
    };
    // React setState functions are stable - don't need them as dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  return { initializingSettings };
};

